import { StyleSheet, View } from "react-native";
import React, { useState, useCallback, useMemo } from "react";
import { useFocusEffect, useRouter } from "expo-router";
import { entitySync } from "@/services/sync/entity-sync";
import { useAuth } from "@components/auth/useAuth";

import ThemedScreen from "@/components/ThemedScreen";
import { CustomAlert } from "@/components/ThemedAlert";
import { ThemedList, type ISortOption, type IGroupOption } from "@/components/ThemedList";
import { ThemedActionHoverPanel, type IBatchAction } from "@/components/ThemedActionHoverPanel";
import { ShareRecipientSelector } from "@/components/sharing/ShareRecipientSelector";

import { useFirebase } from "@components/firebase/useFirebase";
import { getMyPlaces, deletePlace } from "@/services/firebase/place-functions";
import { confirmEntityDeletion } from "@/services/firebase/delete-confirmation";
import { share, unshare, getSharesForSource } from "@/services/firebase/share-functions";
import { getMyContacts } from "@/services/firebase/user-functions";
import { getMyGroups } from "@/services/firebase/group-functions";
import { PlaceModel, ShareModel, Contact, GroupModel } from "common247";
import { PlaceCard } from "@/components/places/PlaceCard";
import { CollapsibleFilterSection } from "@/components/filter";
import { useFilterPersistence } from "@/hooks/useFilterPersistence";
import { filterPlaces, computeFilteredCounts } from "@/services/unified-filter-service";

const PLACE_SORT_OPTIONS: ISortOption<PlaceModel>[] = [
  { key: "name", label: "Place Name (A-Z)", getValue: (p) => p.name },
  { key: "rating", label: "Rating (High-Low)", getValue: (p) => -(p.favouriteRating || 0) },
  { key: "address", label: "Address", getValue: (p) => p.address || "" },
  { key: "createdAt", label: "Date Created", getValue: (p) => -(p.createdAt || 0) },
];

const PLACE_GROUP_OPTIONS: IGroupOption<PlaceModel>[] = [
  {
    key: "role",
    label: "Role",
    getGroupValue: (p) => (p.userRole === "owner" ? "My Places (Owner)" : `Shared with me (${p.userRole || "Viewer"})`),
  },
  {
    key: "rating",
    label: "Rating",
    getGroupValue: (p) => {
      const r = p.favouriteRating || 0;
      return r > 0 ? `${r} Star${r === 1 ? "" : "s"}` : "Unrated";
    },
  },
  {
    key: "tag",
    label: "Primary Tag",
    getGroupValue: (p) => {
      const tags = Object.values(p.tags || {});
      return tags.length > 0 ? tags[0].value : "Untagged";
    },
  },
];

export default function MyPlacesScreen() {
  const services = useFirebase();
  const { user } = useAuth();
  const router = useRouter();

  const [places, setPlaces] = useState<PlaceModel[]>([]);
  const [loading, setLoading] = useState(true);

  // Unified filter persistence
  const { filter, setFilter, resetFilter } = useFilterPersistence("places");

  // Selection state
  const [selectionMode, setSelectionMode] = useState(false);
  const [selectedIds, setSelectedIds] = useState<string[]>([]);
  const [isShareModalOpen, setIsShareModalOpen] = useState(false);

  // Sharing state
  const [sharingPlace, setSharingPlace] = useState<PlaceModel | null>(null);
  const [shares, setShares] = useState<ShareModel[]>([]);
  const [sharesByEntityId, setSharesByEntityId] = useState<Record<string, ShareModel[]>>({});
  const [contacts, setContacts] = useState<Contact[]>([]);
  const [groups, setGroups] = useState<GroupModel[]>([]);
  const [loadingShares, setLoadingShares] = useState(false);

  const effectivePlaces = useMemo(() => {
    return filterPlaces(places, filter);
  }, [places, filter]);

  const filteredCounts = useMemo(() => {
    return computeFilteredCounts({ places }, filter, "places");
  }, [places, filter]);

  const isSelectionActive = selectionMode || selectedIds.length > 0;

  const fetchAllData = useCallback(async () => {
    if (!services) return;
    setLoading(true);
    try {
      const [response, contactsRes, groupsRes] = await Promise.all([
        getMyPlaces(services, { filter }),
        getMyContacts(services, { includeInactive: false }).catch(() => ({ status: false, contacts: [] as Contact[] })),
        getMyGroups(services, {}).catch(() => ({ status: false, groups: [] as GroupModel[] })),
      ]);
      if (response.status) {
        setPlaces(response.places || []);
      }
      if (contactsRes.status && contactsRes.contacts) {
        setContacts(contactsRes.contacts);
      }
      if (groupsRes.status && groupsRes.groups) {
        setGroups(groupsRes.groups);
      }
    } catch (err) {
      console.error("[MyPlacesScreen] Fetch error:", err);
    } finally {
      setLoading(false);
    }
  }, [services, filter]);

  useFocusEffect(
    useCallback(() => {
      fetchAllData();
    }, [fetchAllData])
  );

  // Entity sync listener
  useFocusEffect(
    useCallback(() => {
      const unsub = entitySync.subscribe((event) => {
        if (event.type === "place") {
          fetchAllData();
        }
      });
      return () => unsub();
    }, [fetchAllData])
  );

  const fetchSharesForPlace = async (placeId: string) => {
    if (!services || !placeId) return;
    setLoadingShares(true);
    try {
      const res = await getSharesForSource(services, {
        sourceId: placeId,
        sourceType: "place",
      });
      if (res.status && res.shares) {
        setShares(res.shares);
        setSharesByEntityId((prev) => ({ ...prev, [placeId]: res.shares || [] }));
      }
    } catch (err) {
      console.error("[MyPlacesScreen] Error fetching shares:", err);
    } finally {
      setLoadingShares(false);
    }
  };

  const handleDeletePlace = async (place: PlaceModel) => {
    if (!services || !place.id) return;

    confirmEntityDeletion(
      services,
      "place",
      place.id,
      async () => {
        try {
          const res = await deletePlace(services, { placeId: place.id });
          if (res.status) {
            setPlaces((prev) => prev.filter((p) => p.id !== place.id));
          } else {
            CustomAlert.alert("Error", res.error || "Failed to delete place.");
          }
        } catch (err: any) {
          CustomAlert.alert("Error", err.message || "Deletion failed.");
        }
      },
      place.name
    );
  };

  const handleShareTarget = async (
    targetId: string,
    targetType: "contact" | "group",
    role: "viewer" | "editor"
  ) => {
    if (!services || !sharingPlace?.id) return;
    try {
      const res = await share(services, {
        sourceId: sharingPlace.id,
        sourceType: "place",
        targetId,
        targetType,
        role,
      });
      if (res.status) {
        await fetchSharesForPlace(sharingPlace.id);
      } else {
        CustomAlert.alert("Sharing Error", res.error || "Failed to share place.");
      }
    } catch (err: any) {
      CustomAlert.alert("Sharing Error", err.message || "Failed to share place.");
    }
  };

  const handleUnshare = async (shareId: string) => {
    if (!services || !sharingPlace?.id) return;
    try {
      const res = await unshare(services, { shareId });
      if (res.status) {
        await fetchSharesForPlace(sharingPlace.id);
      } else {
        CustomAlert.alert("Error", res.error || "Failed to revoke share.");
      }
    } catch (err: any) {
      CustomAlert.alert("Error", err.message || "Failed to revoke share.");
    }
  };

  // Batch actions
  const batchActions: IBatchAction[] = [
    {
      id: "share",
      label: "Share",
      icon: "share-social-outline",
      onPress: () => {
        if (selectedIds.length === 0) return;
        const first = places.find((p) => p.id === selectedIds[0]);
        if (first) {
          setSharingPlace(first);
          fetchSharesForPlace(first.id);
          setIsShareModalOpen(true);
        }
      },
    },
    {
      id: "delete",
      label: "Delete",
      icon: "trash-outline",
      destructive: true,
      onPress: () => {
        if (selectedIds.length === 0) return;
        CustomAlert.alert(
          "Delete Selected Places",
          `Are you sure you want to delete ${selectedIds.length} selected place(s)?`,
          [
            { text: "Cancel", style: "cancel" },
            {
              text: "Delete",
              style: "destructive",
              onPress: async () => {
                for (const id of selectedIds) {
                  await deletePlace(services, { placeId: id }).catch(() => {});
                }
                setSelectedIds([]);
                setSelectionMode(false);
                fetchAllData();
              },
            },
          ]
        );
      },
    },
  ];

  const handleClearSelection = () => {
    setSelectedIds([]);
    setSelectionMode(false);
  };

  return (
    <ThemedScreen title="Places" loading={loading} scrollable={false}>
      <View style={styles.container}>
        {/* Unified Filter Section */}
        <CollapsibleFilterSection
          screen="places"
          filter={filter}
          onFilterChange={setFilter}
          counts={filteredCounts}
          onReset={resetFilter}
          allPlaces={places}
          allContacts={contacts as any}
          allGroups={groups}
        />

        {/* List of Places */}
        <ThemedList<PlaceModel>
          data={effectivePlaces}
          loading={loading}
          enableUrlSync={true}
          selectionMode={isSelectionActive}
          selectedIds={selectedIds}
          onSelectedIdsChange={setSelectedIds}
          sortOptions={PLACE_SORT_OPTIONS}
          defaultSortKey="name"
          defaultSortDirection="asc"
          groupOptions={PLACE_GROUP_OPTIONS}
          persistenceKey="my-places"
          entityType="place"
          onRefresh={fetchAllData}
          refreshing={loading}
          addButton={{
            title: isSelectionActive ? "Back" : "+ Add Place",
            onPress: () => (isSelectionActive ? handleClearSelection() : router.push("/(tabs)/my-places/new")),
            variant: isSelectionActive ? "secondary" : "primary",
            testID: "new-place-button",
          }}
          renderItem={(item, _index, isHovered, viewMode) => {
            const isSelected = selectedIds.includes(item.id);
            return (
              <PlaceCard
                place={item}
                viewMode={viewMode}
                isHovered={isHovered}
                selectionMode={isSelectionActive}
                isSelected={isSelected}
                onPress={() => {
                  if (isSelectionActive) {
                    setSelectedIds((prev) =>
                      prev.includes(item.id) ? prev.filter((id) => id !== item.id) : [...prev, item.id]
                    );
                  } else {
                    router.push(`/(tabs)/my-places/${item.id}`);
                  }
                }}
                onEditPress={() => router.push(`/(tabs)/my-places/${item.id}`)}
                onDelete={() => handleDeletePlace(item)}
                onShare={() => {
                  setSharingPlace(item);
                  fetchSharesForPlace(item.id);
                  setIsShareModalOpen(true);
                }}
                shares={sharesByEntityId[item.id]}
                availableContacts={contacts as any}
                availableGroups={groups}
                availablePlaces={places}
                isLoadingShares={loadingShares && sharingPlace?.id === item.id}
                onShareTarget={handleShareTarget}
                onUnshare={handleUnshare}
                currentUserId={user?.uid}
              />
            );
          }}
          keyExtractor={(item) => item.id}
          emptyText="No places found."
        />

        {/* Batch Selection Action Hover Panel */}
        {isSelectionActive && (
          <ThemedActionHoverPanel
            visible={isSelectionActive}
            selectedCount={selectedIds.length}
            actions={batchActions}
            onClearSelection={() => {
              setSelectedIds([]);
              setSelectionMode(false);
            }}
          />
        )}

        {/* Share Recipient Selector Modal */}
        {isShareModalOpen && sharingPlace && (
          <ShareRecipientSelector
            visible={isShareModalOpen}
            onClose={() => {
              setIsShareModalOpen(false);
              setSharingPlace(null);
            }}
            sourceId={sharingPlace.id}
            sourceType="place"
            shares={shares}
            availableContacts={contacts}
            availableGroups={groups}
            isLoading={loadingShares}
            onShare={handleShareTarget}
            onUnshare={handleUnshare}
          />
        )}
      </View>
    </ThemedScreen>
  );
}

const styles = StyleSheet.create({
  container: {
    flex: 1,
  },
  headerActions: {
    flexDirection: "row",
    gap: 8,
    alignItems: "center",
  },
});
